import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import Datetime from "react-datetime";
import { useSelector } from "react-redux";
import { createItem, getList, updateItem } from "../../api/generics";
import {
  TreasuryAccountOption,
  TreasuryMovement,
} from "../../models/treasury";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, warningAlert } from "../utils/messages";

interface TreasuryMovementFormProps {
  show: boolean;
  // Con movimiento se edita (solo pendientes cargados a mano); sin, se crea.
  movement?: TreasuryMovement;
  onCancel: () => void;
  onSuccess: () => void;
}

// Carga y edicion de movimientos pendientes. No muestra saldos: lo usan
// tambien los operadores, que no ven las cuentas.
const TreasuryMovementForm: React.FC<TreasuryMovementFormProps> = ({
  show,
  movement,
  onCancel,
  onSuccess,
}) => {
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );
  const [accounts, setAccounts] = useState<TreasuryAccountOption[]>([]);
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState<string>("");
  const [accountId, setAccountId] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [movementDate, setMovementDate] = useState<string | moment.Moment>(
    moment()
  );
  const [submitting, setSubmitting] = useState(false);

  const fetchAccounts = async () => {
    const accountsStatus = await getList<TreasuryAccountOption>(
      "/treasury/accounts/options/",
      -1,
      0,
      new Map([["company_id", companyId?.toString()]])
    );
    if (accountsStatus.status === SUCCESS && accountsStatus.data) {
      setAccounts(accountsStatus.data.items);
    } else {
      warningAlert(accountsStatus.detail ?? "Error desconocido");
    }
  };

  useEffect(() => {
    if (!show) {
      return;
    }
    if (movement !== undefined) {
      setDirection(Number(movement.amount) < 0 ? "out" : "in");
      setAmount(String(Math.abs(Number(movement.amount))));
      setAccountId(
        movement.suggestedAccountId ? String(movement.suggestedAccountId) : ""
      );
      setDescription(movement.description ?? "");
      setReason(movement.reason ?? "");
      setMovementDate(moment(movement.movementDate));
    } else {
      setDirection("in");
      setAmount("");
      setAccountId("");
      setDescription("");
      setReason("");
      setMovementDate(moment());
    }
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, movement]);

  const onSave = async () => {
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      errorAlert("Indique un monto mayor a 0");
      return;
    }
    if (accountId === "") {
      errorAlert("Seleccione la cuenta destino");
      return;
    }
    if (description.trim() === "") {
      errorAlert("El origen es obligatorio");
      return;
    }
    if (!moment.isMoment(movementDate) || !movementDate.isValid()) {
      errorAlert("Indique una fecha válida");
      return;
    }
    const body = {
      companyId,
      direction,
      amount: numericAmount,
      accountId: Number(accountId),
      description: description.trim(),
      reason: reason.trim() === "" ? undefined : reason.trim(),
      movementDate: movementDate.toISOString(),
    };
    setSubmitting(true);
    const requestStatus =
      movement === undefined
        ? await createItem("/treasury/movements/create/", body)
        : await updateItem(`/treasury/movements/${movement.id}/`, body);
    setSubmitting(false);
    if (requestStatus.status !== SUCCESS) {
      errorAlert(requestStatus.detail ?? "Ha ocurrido un error!!");
      return;
    }
    onSuccess();
  };

  return (
    <CModal show={show} onClose={onCancel}>
      <CModalHeader closeButton>
        <CModalTitle>
          {movement === undefined
            ? "Nuevo movimiento"
            : `Editar movimiento #${movement.id}`}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <fieldset disabled={submitting}>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Tipo:</CLabel>
              </CCol>
              <CCol>
                <CSelect
                  value={direction}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setDirection(e.target.value as "in" | "out")
                  }
                >
                  <option value="in">Ingreso (suma a la cuenta)</option>
                  <option value="out">Egreso (resta de la cuenta)</option>
                </CSelect>
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Monto:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  type="number"
                  min={0}
                  value={amount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAmount(e.target.value)
                  }
                />
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Cuenta:</CLabel>
              </CCol>
              <CCol>
                <CSelect
                  value={accountId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setAccountId(e.target.value)
                  }
                >
                  <option value="" disabled>
                    -----
                  </option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </CSelect>
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Origen:</CLabel>
              </CCol>
              <CCol>
                <CInput
                  type="text"
                  value={description}
                  maxLength={1023}
                  placeholder="Ej: Recibido de sucursal X"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDescription(e.target.value)
                  }
                />
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Fecha:</CLabel>
              </CCol>
              <CCol>
                <Datetime
                  onChange={setMovementDate}
                  value={movementDate}
                  locale="es/PY"
                  dateFormat="DD/MM/YYYY"
                  timeFormat="HH:mm"
                  closeOnSelect={true}
                ></Datetime>
              </CCol>
            </CRow>
          </CFormGroup>
          <CFormGroup>
            <CRow>
              <CCol md={3}>
                <CLabel>Observación:</CLabel>
              </CCol>
              <CCol>
                <CTextarea
                  rows={2}
                  value={reason}
                  maxLength={1023}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setReason(e.target.value)
                  }
                  placeholder="Opcional"
                />
              </CCol>
            </CRow>
          </CFormGroup>
        </fieldset>
      </CModalBody>
      <CModalFooter>
        <CButtonGroup>
          <CButton color="secondary" onClick={onCancel}>
            Cancelar
          </CButton>
          <CButton color="primary" onClick={onSave} disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CButtonGroup>
      </CModalFooter>
    </CModal>
  );
};

export default TreasuryMovementForm;
